import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

/** Call facts the call_history trigger puts on a phone_call activity. */
export interface CallActivityData {
  call_direction?: string;
  call_status?: string;
  duration_seconds?: number;
  talk_duration_seconds?: number;
  phone_number?: string;
  answered_by_ai?: boolean;
  has_recording?: boolean;
  transcription_status?: string;
  summary?: string;
  transcript?: string;
  transcript_truncated?: boolean;
  /** Set by a Sales call play when the owner pressed Done. */
  via?: string;
  talking_points?: string[];
}

/** The receptionist prepends "Summary: …" as the first paragraph; the summary field already carries it. */
function transcriptBody(transcript: string): string {
  const stripped = transcript.replace(/^\s*summary:[^\n]*(\n[^\n]+)*\n\s*\n/i, '').trim();
  return stripped || transcript.trim();
}

/** Short label for the call row: who called, whether it connected, how long. */
export function callOutcomeLabel(data: CallActivityData): string {
  const inbound = String(data.call_direction || '').toLowerCase() === 'inbound';
  const status = String(data.call_status || '').toLowerCase();
  const seconds = Number(data.talk_duration_seconds) || Number(data.duration_seconds) || 0;
  const missed =
    ['no-answer', 'busy', 'failed', 'canceled', 'cancelled', 'missed', 'voicemail'].includes(status) ||
    (status === 'completed' && seconds === 0 && !data.transcript);
  if (missed) return inbound ? 'Missed call' : 'No answer';
  const length = seconds ? (seconds < 90 ? `${seconds}s` : `${Math.round(seconds / 60)} min`) : null;
  const who = inbound ? (data.answered_by_ai ? 'Incoming · answered by AI' : 'Incoming') : 'Outgoing';
  return length ? `${who} · ${length}` : who;
}

/**
 * What was said on a call, under the call row in the lead history: the
 * summary, and the transcript behind a Show transcript toggle. When the
 * business does not record calls, says so in one quiet line.
 */
export default function CrmCallActivityBody({ data }: { data: CallActivityData }) {
  const [open, setOpen] = useState(false);
  const summary = String(data.summary || '').trim();
  const transcript = String(data.transcript || '').trim();
  const points = Array.isArray(data.talking_points) ? data.talking_points.filter(Boolean) : [];
  const connected = !/^(Missed|No answer)/.test(callOutcomeLabel(data));

  if (!summary && !transcript && !points.length) {
    if (!connected) return null;
    return (
      <div className="mt-1 text-[11.5px] text-gray-400 leading-snug">
        {data.has_recording ? 'Recorded · transcript pending' : 'No transcript · call recording is off'}
      </div>
    );
  }

  const body = transcript ? transcriptBody(transcript) : '';
  const showSummary = summary && (!body || summary !== body.slice(0, summary.length));

  return (
    <div className="mt-1 space-y-1">
      {showSummary ? (
        <div className="whitespace-pre-wrap break-words rounded-lg bg-orange-50/60 px-2.5 py-1.5 text-[13px] leading-snug text-gray-800">
          {summary}
        </div>
      ) : null}
      {points.length ? (
        <ul className="rounded-lg bg-gray-50 px-2.5 py-1.5 text-[12.5px] leading-snug text-gray-700 space-y-0.5">
          {points.map((point, index) => (
            <li key={index} className="flex gap-1.5">
              <span className="text-emerald-600 shrink-0">•</span>
              <span>{point}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {body ? (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setOpen((current) => !current);
            }}
            className="inline-flex items-center gap-1 text-[11.5px] font-medium text-orange-700 hover:text-orange-800"
          >
            {open ? <ChevronUp className="h-3 w-3" aria-hidden /> : <ChevronDown className="h-3 w-3" aria-hidden />}
            {open ? 'Hide transcript' : 'Show transcript'}
          </button>
          {open ? (
            <div className="whitespace-pre-wrap break-words rounded-lg bg-gray-50 px-2.5 py-1.5 text-[12.5px] leading-snug text-gray-700 max-h-72 overflow-y-auto">
              {body}
              {data.transcript_truncated ? <div className="mt-1 text-[11px] text-gray-400">Transcript continues in Messages.</div> : null}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
